
import React from 'react';
import { withStyles } from '@material-ui/core';
import SelectContainer from 'components/SelectContainer';
import { usePOContextSelector, usePODispatch } from '../PurchaseOrders.context';
import { IUpdateStatusProperties } from './UpdateStatus.properties';
import styles from './UpdateStatus.styles';
import { PopUp } from 'components/common/SummaryTable/PopUp/PopUp';
import { useUpdatePurchaseOrdersStatus } from 'api/purchaseOrders/purchaseOrders';
import { isScreenRequest } from 'api/utils';
import UserIdScreenDialog from 'components/common/UserIdScreenDialog';
import { PurchaseOrdersActionTypes } from '../PurchaseOrders.actions';
import { useSnackBar } from 'components/common/SnackBars/SnackBar.hooks';
import classNames from 'classnames';

const StyledPopUp = withStyles(styles, { index: 1 })(PopUp);
const UpdateStatus = (props: IUpdateStatusProperties): JSX.Element => {

    const { classes, Alert, AlertMessage, PurchaseOrderStatus } = props;
    const [value, setValue] = React.useState('');
    const [reload, setReload] = React.useState<boolean>(false);
    const [userIdDialogOpen, setUserIdDialogOpen] = React.useState<boolean>(false);
    const [statusChange, setChangedStatus] = React.useState<boolean>(false);
    const PurchaseOrder = usePOContextSelector<'PurchaseOrder'>((state) => state.PurchaseOrder);
    const PreventChangeStatus = usePOContextSelector<'PreventChangeStatus'>((state) => state.PreventChangeStatus);
    const IsChangeStatusDisabled = usePOContextSelector<'IsChangeStatusDisabled'>((state) => state.IsChangeStatusDisabled);
    const contextDispatch = usePODispatch();
    const { showSnackBar } = useSnackBar();

    React.useEffect(
        () => {
            if (reload) {
                contextDispatch({
                    [PurchaseOrdersActionTypes.setReloadPurchaseOrder]: reload,
                });
            }
        },
        [reload]
    );

    const updateStatusMutation = useUpdatePurchaseOrdersStatus();

    const SelectLookupAPI = React.useMemo(
        () => {
            if (PurchaseOrder === '') return { apiPrefs: null, params: null };

            return {
                apiPrefs: {
                    LookupType: 'Dynamic',
                    path: '/CustomTypes/Lookup/PurchaseOrderStatusOption/Search',
                    method: 'POST'
                },
                params: {
                    SearchText: '',
                    PurchaseOrder
                }
            };
        },
        [PurchaseOrder, reload]
    );

    React.useEffect(
        () => {
            setValue(PurchaseOrderStatus?.toUpperCase());
        },
        [PurchaseOrder, PurchaseOrderStatus]
    );

    const _updateStatus = async (status: any, userId?: number): Promise<void> => {
        if (PreventChangeStatus) {
            showSnackBar({ variant: 'warning', message: 'Please save or discard changes.' });
        } else {
            await updateStatus(status, userId);
        }
    };

    const updateStatus = async (status: any, userId?: number): Promise<void> => {
        if (status && status !== value && value !== '') {
            const response = await updateStatusMutation.mutateAsync({
                PurchaseOrder: PurchaseOrder,
                SetStatus: status,
                urlQueryParams: {
                    UserId: userId?.toString() || ''
                }
            });

            if (isScreenRequest(response?.Forms, 'POStatusUser')) {
                setUserIdDialogOpen(true);
                setChangedStatus(status);
            }

            if (response?.Status) {
                setReload(true);
                setValue(status);
                setChangedStatus(null);
                setUserIdDialogOpen(false);
            }
        }
    };

    return (
        <div className={IsChangeStatusDisabled ? classNames(classes.container, classes.containerDisabled) : classes.container}>
            <SelectContainer
                onChange={_updateStatus}
                apiPrefs={PurchaseOrder && SelectLookupAPI.apiPrefs}
                params={PurchaseOrder && SelectLookupAPI.params}
                disabled={IsChangeStatusDisabled}
                value={value} />
            {Alert &&
                <StyledPopUp color='Amber' icon='Warning' message={AlertMessage} title='Alert' />
            }
            <UserIdScreenDialog
                open={userIdDialogOpen}
                title={'Order Status Change Confirmation'}
                message={'Enter User ID to confirm the status change'}
                loading={false}
                disablePin={true}
                data={{
                    UserId: null,
                }}
                onOk={(data) => { _updateStatus(statusChange, data?.UserId); }}
                onCancel={() => setUserIdDialogOpen(false)}
            />
        </div>
    );
};

export default withStyles(styles, { index: 1 })(UpdateStatus);
